import React from 'react';
import { Box, Container, Typography, Divider, Stack, Button } from '@mui/material';

export default function TermsAndConditionsPage() {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: { xs: 4, md: 6 } }}>
      <Container maxWidth="md">
        <Stack spacing={2.5}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="h3" fontWeight={900} gutterBottom>
                Terms & Conditions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Effective date: {new Date().toLocaleDateString()}
              </Typography>
            </Box>
            <Button variant="outlined" href="/">
              Back to Home
            </Button>
          </Box>

          <Divider />

          <Typography variant="body1" color="text.secondary">
            These Terms & Conditions ("Terms") govern access to and use of this school management platform (the "Platform"),
            including all related websites, applications, and services provided through it (collectively, the "Services").
            By creating an account, registering a school, or using any part of the Services, you agree to be bound by these Terms.
          </Typography>

          <Stack spacing={2}>
            <Section title="1. Definitions">
              <Typography variant="body2" color="text.secondary">
                "School" means the educational institution registering to use the Services.
                "You" means the School, its authorized administrators, staff members, and any other users acting on its behalf.
                "We", "Us", "Our" means the Service provider operating the Platform.
                "End Users" may include staff, students, parents/guardians, and other authorized roles.
              </Typography>
            </Section>

            <Section title="2. Eligibility and Account Responsibility">
              <Typography variant="body2" color="text.secondary">
                You represent that you have the authority to accept these Terms for the School.
                You are responsible for maintaining the confidentiality of your login credentials and for all activities
                carried out under your accounts. You must promptly notify us of any suspected unauthorized access.
              </Typography>
            </Section>

            <Section title="3. Services, Trial, Subscription and Billing">
              <Typography variant="body2" color="text.secondary">
                The Platform may be offered under a free trial period and/or paid subscription plans. During a free trial,
                you may access the Services without payment for the trial duration specified on the Platform.
                If you choose to continue after the trial, you may be required to purchase a subscription.
                
                Subscription fees (if applicable) are charged according to the plan you select. Taxes may apply depending on
                your jurisdiction. Failure to renew a subscription after expiry may lead to reduced access, grace period restrictions,
                and/or suspension according to the Platform’s policies.
              </Typography>
            </Section>

            <Section title="4. Acceptable Use">
              <Typography variant="body2" color="text.secondary">
                You agree not to:
                
                - Use the Services for unlawful, harmful, or fraudulent purposes
                - Attempt to gain unauthorized access to any systems, accounts, or data
                - Interfere with or disrupt the integrity or performance of the Services
                - Upload malware, malicious code, or exploit vulnerabilities
                - Reverse engineer, copy, or resell the Services except as permitted by law
              </Typography>
            </Section>

            <Section title="5. Data Protection and Privacy">
              <Typography variant="body2" color="text.secondary">
                The School is responsible for ensuring it has the legal basis to collect, store, and process personal data
                of students, staff, parents/guardians, and other End Users.
                
                We implement reasonable technical and organizational safeguards designed to protect data, but no method of
                transmission or storage is 100% secure. You agree to use the Services in a way that supports privacy and security,
                including limiting account access only to authorized persons.
              </Typography>
            </Section>

            <Section title="6. User Content and Ownership">
              <Typography variant="body2" color="text.secondary">
                As between you and us, you retain ownership of your data and content you upload or generate within the Platform
                ("Customer Data"). You grant us a limited right to process Customer Data solely to provide, maintain, and improve
                the Services, comply with applicable law, and enforce these Terms.
              </Typography>
            </Section>

            <Section title="7. Third-Party Services">
              <Typography variant="body2" color="text.secondary">
                The Services may integrate with third-party providers (e.g., email/SMS gateways, payment providers).
                Your use of such third-party services may be subject to their own terms and policies.
                We are not responsible for third-party services beyond the extent required by applicable law.
              </Typography>
            </Section>

            <Section title="8. Availability, Maintenance, and Changes">
              <Typography variant="body2" color="text.secondary">
                We may perform maintenance or updates that affect availability. We may modify, add, or remove features
                to improve the Platform or comply with legal requirements.
              </Typography>
            </Section>

            <Section title="9. Suspension and Termination">
              <Typography variant="body2" color="text.secondary">
                We may suspend or terminate access to the Services if:
                
                - You violate these Terms
                - There is suspected abuse, fraud, or security risk
                - Subscription obligations are not met after applicable grace periods
                
                Upon termination, your right to access the Services ceases. Depending on applicable law and our policies,
                we may allow a limited data export window.
              </Typography>
            </Section>

            <Section title="10. Disclaimer">
              <Typography variant="body2" color="text.secondary">
                The Services are provided on an "as is" and "as available" basis. To the maximum extent permitted by law,
                we disclaim all warranties, express or implied, including merchantability, fitness for a particular purpose,
                and non-infringement.
              </Typography>
            </Section>

            <Section title="11. Limitation of Liability">
              <Typography variant="body2" color="text.secondary">
                To the maximum extent permitted by law, we will not be liable for indirect, incidental, special, consequential,
                or punitive damages, or any loss of data, profits, revenue, or business opportunities.
                Our total liability for any claim related to the Services will not exceed the amounts paid by you to us for
                the Services in the twelve (12) months preceding the event giving rise to the claim (or, if none, a reasonable
                amount determined under applicable law).
              </Typography>
            </Section>

            <Section title="12. Indemnity">
              <Typography variant="body2" color="text.secondary">
                You agree to defend, indemnify, and hold harmless us and our affiliates, officers, employees, and agents from
                and against claims, damages, liabilities, and expenses arising out of your use of the Services, your violation
                of these Terms, or your violation of any rights of a third party.
              </Typography>
            </Section>

            <Section title="13. Governing Law and Dispute Resolution">
              <Typography variant="body2" color="text.secondary">
                These Terms are governed by the laws of the Republic of Cameroon, and where applicable, relevant OHADA
                Uniform Acts. If a dispute arises, the parties will first attempt to resolve it through good-faith
                negotiations.

                If the dispute is not resolved, the parties may submit the matter to the competent courts in Cameroon.
                Where applicable under OHADA, disputes may also be subject to the Common Court of Justice and Arbitration
                (CCJA) and/or other OHADA dispute resolution mechanisms.

                If these Terms are translated, the English or French version may be used for interpretation to the extent
                permitted by applicable law and depending on the version presented in the Platform.
              </Typography>
            </Section>

            <Section title="14. Changes to These Terms">
              <Typography variant="body2" color="text.secondary">
                We may update these Terms from time to time. If changes are material, we may provide notice within the Platform.
                Continued use of the Services after changes become effective constitutes acceptance.
              </Typography>
            </Section>

            <Section title="15. Contact">
              <Typography variant="body2" color="text.secondary">
                For questions about these Terms, contact your system administrator or the Platform support team.
              </Typography>
            </Section>
          </Stack>

          <Divider />

          <Typography variant="caption" color="text.secondary">
            This Terms & Conditions document is provided as a general template for software platforms and should be reviewed
            by qualified legal counsel to ensure compliance with your local laws and operational policies.
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
}

function Section({ title, children }) {
  return (
    <Box>
      <Typography variant="h6" fontWeight={800} sx={{ mb: 0.75 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}
